import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ToggleMenuIcon from '../Icons/ToggleMenuIcon';
import BuildBaseIcon from '../Icons/BuildBaseIcon';
import { getMenuContent } from '../api-sanity/api';
import '../styles/components/menu.css';
import { useTheme } from '../hooks/useTheme';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { interpolateColor, isMobileDevice } from 'utils/helpers';
import useMediaQuery from 'hooks/useMediaQuery';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
const defaultIconColor = '#FAF7F2';
const zoneIconColor = '#2E4753';

const Menu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery();
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDarkMode } = useTheme();
  const [menuData, setMenuData] = useState(null);
  const [currentImagePath, setCurrentImagePath] = useState(null);
  const [isFirstExpansion, setIsFirstExpansion] = useState(true);
  const [preloadedImages, setPreloadedImages] = useState({});
  const menuRef = useRef(null);
  const menuLinksRef = useRef([]);
  const imageContainerRef = useRef(null);
  const toggleMenuIconRef = useRef(null);
  const buildIconRef = useRef(null);
  const lastAnimatedImageRef = useRef(null);
  const animationRef = useRef();
  const scrollProgressBeforeExpand = useRef(0);
  const isRestoringProgressRef = useRef(false);

  const [buildIconColor, setBuildIconColor] = useState(defaultIconColor);

  const getFirstRoute = useCallback(() => {
    const segments = location.pathname.split('/');
    return segments.length > 1 ? `/${segments[1]}` : '/';
  }, [location.pathname]);

  useEffect(() => {
    const fetchMenuData = async () => {
      const data = await getMenuContent();
      if (!data.error) setMenuData(data);
    };
    fetchMenuData();
  }, []);

  useGSAP(
    () => {
      const smoother = ScrollSmoother.get();
      if (smoother) {
        smoother.paused(isExpanded);
      }
    },
    { dependencies: [isExpanded] }
  );

  const getImageUrl = useCallback(
    (path) => {
      if (!menuData) return '';
      const imageMap = {
        '/services': {
          dark: menuData.servicesDarkImageUrl,
          light: menuData.servicesLightImageUrl,
        },
        '/about': {
          dark: menuData.aboutDarkImageUrl,
          light: menuData.aboutLightImageUrl,
        },
        '/': {
          dark: menuData.homeDarkImageUrl,
          light: menuData.homeLightImageUrl,
        },
      };
      return (
        imageMap[path]?.[isDarkMode ? 'dark' : 'light'] ||
        imageMap['/'][isDarkMode ? 'dark' : 'light']
      );
    },
    [isDarkMode, menuData]
  );

  useLayoutEffect(() => {
    if (menuData) {
      const imageUrls = [
        menuData.servicesDarkImageUrl,
        menuData.servicesLightImageUrl,
        menuData.aboutDarkImageUrl,
        menuData.aboutLightImageUrl,
        menuData.homeDarkImageUrl,
        menuData.homeLightImageUrl,
      ].filter(Boolean);

      const loadedImages = {};
      let imagesToLoad = imageUrls.length;

      if (imagesToLoad === 0) {
        setPreloadedImages({});
        return;
      }

      imageUrls.forEach((url) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
          loadedImages[url] = true;
          imagesToLoad--;
          if (imagesToLoad === 0) {
            setPreloadedImages(loadedImages);
          }
        };
        img.onerror = () => {
          console.error(`Failed to load image: ${url}`);
          imagesToLoad--;
          if (imagesToLoad === 0) {
            setPreloadedImages(loadedImages);
          }
        };
      });
    }
  }, [menuData]);

  useLayoutEffect(() => {
    setCurrentImagePath(getFirstRoute());
    if (isExpanded) {
      if (isFirstExpansion) {
        setIsFirstExpansion(false);
      }
    } else {
      setIsFirstExpansion(true);

      if (imageContainerRef.current) {
        gsap.to(imageContainerRef.current.children, {
          opacity: 0,
          duration: 0.3,
          onComplete: () => {
            imageContainerRef.current.innerHTML = '';
          },
        });
      }
    }
  }, [isExpanded, isFirstExpansion, getFirstRoute]);

  useGSAP(
    () => {
      if (!isExpanded || !menuLinksRef.current) return;

      const validLinks = menuLinksRef.current.filter(Boolean);

      if (validLinks.length === 0) return;

      gsap.set(validLinks, {
        clipPath: 'inset(100% 0 0 0)',
        scale: 0.9,
      });

      const tl = gsap.timeline();

      tl.to(validLinks, {
        clipPath: 'inset(0% 0 0 0)',
        duration: 0.9,
        ease: 'power2.out',
        stagger: 0.05,
        overwrite: 'auto',
      }).to(
        validLinks,
        {
          scale: 1,
          duration: 0.9,
          ease: 'power2.out',
          stagger: 0.05,
          overwrite: 'auto',
        },
        '<'
      );

      return () => {
        tl.kill();
      };
    },
    { dependencies: [isExpanded], scope: menuLinksRef }
  );

  useGSAP(
    () => {
      if (!currentImagePath || !imageContainerRef.current || !isExpanded)
        return;

      const newUrl = getImageUrl(currentImagePath);
      const imageContainerEl = imageContainerRef.current;

      if (!preloadedImages[newUrl] && newUrl) {
        console.warn(`Image not yet preloaded: ${newUrl}. Skipping animation.`);
        return;
      }

      const newImageEl = document.createElement('img');
      newImageEl.src = newUrl;
      newImageEl.alt = 'Menu background';
      newImageEl.style.position = 'absolute';
      newImageEl.style.zIndex = imageContainerEl.children.length + 1;

      imageContainerEl.appendChild(newImageEl);

      const tl = gsap.timeline({
        onComplete: () => {
          gsap.set(newImageEl, { opacity: 1, zIndex: 1 });
          lastAnimatedImageRef.current = newImageEl;
        },
      });

      tl.fromTo(
        newImageEl,
        {
          clipPath: 'inset(100% 0 0 0)',
          scale: 0.9,
          opacity: 1,
        },
        {
          clipPath: 'inset(0% 0 0 0)',
          scale: 1,
          duration: 0.9,
          ease: 'power2.out',
          overwrite: 'auto',
        }
      );

      return () => {
        tl.kill();

        if (newImageEl.parentNode === imageContainerEl) {
          imageContainerEl.removeChild(newImageEl);
        }

        if (lastAnimatedImageRef.current === newImageEl) {
          lastAnimatedImageRef.current = null;
        }
      };
    },
    {
      dependencies: [currentImagePath, isExpanded, preloadedImages],
      scope: imageContainerRef,
    }
  );

  useGSAP(() => {
    const mm = gsap.matchMedia(menuRef);

    mm.add(`screen and (min-width: 768px)`, () => {
      if (getFirstRoute() !== '/' || isMobileDevice()) {
        return;
      }

      const icon = buildIconRef.current;
      const menu = menuRef.current;
      const getScrollDistance = () => window.innerHeight * 0.8;

      ScrollTrigger.getById('buildIconPosition')?.kill();

      gsap.set(icon, {
        position: 'absolute',
        width: '51.78dvw',
        height: 'auto',
        y: getScrollDistance(),
        zIndex: 1,
      });

      const tl = gsap.to(icon, {
        y: 0,
        width: '137px',
        ease: 'power2.out',
      });

      ScrollTrigger.create({
        id: 'buildIconPosition',
        animation: tl,
        trigger: menu,
        start: 'top top',
        end: () => `+=${getScrollDistance()}`,
        scrub: 0.1,
        invalidateOnRefresh: false,
        onRefresh: () => {
          const st = ScrollTrigger.getById('buildIconPosition');
          const currentProgress = st?.progress || 0;
          if (currentProgress > 0 && !isRestoringProgressRef.current) {
            tl.progress(currentProgress);
          }
        },
      });

      return () => {
        ScrollTrigger.getById('buildIconPosition')?.kill();
        gsap.set(icon, { clearProps: 'all' });
      };
    });
  }, [getFirstRoute]);

  useGSAP(
    () => {
      const st = ScrollTrigger.getById('buildIconPosition');
      if (!st) {
        return;
      }

      const timeline = st.animation;
      if (!timeline) {
        return;
      }

      gsap.killTweensOf(timeline, 'progress');

      if (isExpanded) {
        scrollProgressBeforeExpand.current = st.progress;

        st.disable();

        timeline.progress(1);
      } else {
        const targetProgress = scrollProgressBeforeExpand.current;

        isRestoringProgressRef.current = true;
        st.disable();

        requestAnimationFrame(() => {
          st.refresh();

          const originalScrub = st.vars.scrub;
          st.vars.scrub = false;

          gsap.to(timeline, {
            progress: targetProgress,
            duration: 0.6,
            ease: 'power2.inOut',
            overwrite: true,
            onComplete: () => {
              timeline.progress(targetProgress);

              requestAnimationFrame(() => {
                st.vars.scrub = originalScrub;
                st.enable();
                timeline.progress(targetProgress);

                requestAnimationFrame(() => {
                  timeline.progress(targetProgress);

                  requestAnimationFrame(() => {
                    isRestoringProgressRef.current = false;
                  });
                });
              });
            },
          });
        });
      }
    },
    { dependencies: [isExpanded] }
  );

  const getLinkClassName = (path) => {
    const isActive = currentImagePath === path;
    return `menu-link ${isActive ? 'menu-link-active' : ''} ${
      isDarkMode ? 'dark' : ''
    }`;
  };

  useGSAP(() => {
    if (!buildIconRef.current) return;

    const currentIcon = buildIconRef.current;

    const cancelAnimation = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };

    if (isExpanded) {
      cancelAnimation();

      if (buildIconColor !== defaultIconColor) {
        gsap.to(currentIcon, {
          color: defaultIconColor,
          duration: 0.2,
        });
        setBuildIconColor(defaultIconColor);
      }

      return cancelAnimation;
    }

    const checkOverlapAndChangeColor = () => {
      if (!currentIcon) {
        cancelAnimation();
        return;
      }

      const iconRect = currentIcon.getBoundingClientRect();
      const darkMenuSections = document.querySelectorAll('.dark-menu-section');
      const lightIconZones = document.querySelectorAll('.light-icon-zone');

      let maxDarkOverlapPercentage = 0;
      let isOverlappingLightZone = false;

      darkMenuSections.forEach((zone) => {
        const zoneRect = zone.getBoundingClientRect();
        const intersectLeft = Math.max(iconRect.left, zoneRect.left);
        const intersectRight = Math.min(iconRect.right, zoneRect.right);
        const intersectTop = Math.max(iconRect.top, zoneRect.top);
        const intersectBottom = Math.min(iconRect.bottom, zoneRect.bottom);

        const intersectionWidth = Math.max(0, intersectRight - intersectLeft);
        const intersectionHeight = Math.max(0, intersectBottom - intersectTop);
        const intersectionArea = intersectionWidth * intersectionHeight;
        const iconArea = iconRect.width * iconRect.height;

        const currentOverlapPercentage =
          iconArea > 0 ? intersectionArea / iconArea : 0;
        if (currentOverlapPercentage > maxDarkOverlapPercentage) {
          maxDarkOverlapPercentage = currentOverlapPercentage;
        }
      });

      const MIN_LIGHT_OVERLAP_THRESHOLD = 0.001;
      for (let i = 0; i < lightIconZones.length; i++) {
        const zone = lightIconZones[i];
        const zoneRect = zone.getBoundingClientRect();
        const intersectLeft = Math.max(iconRect.left, zoneRect.left);
        const intersectRight = Math.min(iconRect.right, zoneRect.right);
        const intersectTop = Math.max(iconRect.top, zoneRect.top);
        const intersectBottom = Math.min(iconRect.bottom, zoneRect.bottom);

        const intersectionWidth = Math.max(0, intersectRight - intersectLeft);
        const intersectionHeight = Math.max(0, intersectBottom - intersectTop);
        const intersectionArea = intersectionWidth * intersectionHeight;
        const iconArea = iconRect.width * iconRect.height;

        const currentLightOverlapPercentage =
          iconArea > 0 ? intersectionArea / iconArea : 0;
        if (currentLightOverlapPercentage > MIN_LIGHT_OVERLAP_THRESHOLD) {
          isOverlappingLightZone = true;
          break;
        }
      }

      let effectiveOverlapPercentage = maxDarkOverlapPercentage;
      if (isOverlappingLightZone || maxDarkOverlapPercentage < 0.01) {
        effectiveOverlapPercentage = 0;
      }

      const newColor = interpolateColor(
        defaultIconColor,
        zoneIconColor,
        effectiveOverlapPercentage
      );

      if (newColor !== buildIconColor) {
        gsap.to(currentIcon, { color: newColor, duration: 0 });
        setBuildIconColor(newColor);
      }

      animationRef.current = requestAnimationFrame(checkOverlapAndChangeColor);
    };

    animationRef.current = requestAnimationFrame(checkOverlapAndChangeColor);

    return cancelAnimation;
  }, [isExpanded, defaultIconColor, zoneIconColor, buildIconColor]);

  return (
    <>
      <div ref={menuRef} className='menu-header'>
        <BuildBaseIcon
          ref={buildIconRef}
          height='100%'
          color={buildIconColor}
          width={isMobileSize ? 160 : 137}
          onClick={() =>
            isExpanded && getFirstRoute() === '/'
              ? setIsExpanded(false)
              : navigate('/')
          }
          className='cursor-pointer'
        />
        <ToggleMenuIcon
          color={buildIconColor}
          isExpanded={isExpanded}
          onClick={() => setIsExpanded((prev) => !prev)}
          className='cursor-pointer toggle-menu-icon'
          ref={toggleMenuIconRef}
          width={isMobileSize ? 30 : 29}
          height={isMobileSize ? 30 : 29}
        />
      </div>
      {isExpanded && (
        <div className={`menu-content ${isDarkMode ? 'dark' : ''}`}>
          <div className='menu-nav-wrapper'>
            <nav
              className='menu-nav flex'
              onMouseLeave={() => setCurrentImagePath(getFirstRoute())}
            >
              <Link
                ref={(el) => (menuLinksRef.current[0] = el)}
                to='/'
                className={getLinkClassName('/')}
                onMouseEnter={() => setCurrentImagePath('/')}
                onClick={() => setIsExpanded(getFirstRoute() !== '/')}
              >
                {menuData?.homeLink}
              </Link>
              <Link
                ref={(el) => (menuLinksRef.current[1] = el)}
                to='/about'
                className={getLinkClassName('/about')}
                onMouseEnter={() => setCurrentImagePath('/about')}
                onClick={() => setIsExpanded(getFirstRoute() !== '/about')}
              >
                {menuData?.aboutLink}
              </Link>
              <Link
                ref={(el) => (menuLinksRef.current[2] = el)}
                to='/services'
                className={getLinkClassName('/services')}
                onMouseEnter={() => setCurrentImagePath('/services')}
                onClick={() => setIsExpanded(getFirstRoute() !== '/services')}
              >
                {menuData?.servicesLink}
              </Link>
            </nav>
            <div className='menu-footer'>{menuData?.copyrightText}</div>
          </div>
          <div className='image-wrapper'>
            <div
              ref={imageContainerRef}
              className='relative full-height full-width overflow-hidden'
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Menu;
